import React, { useState, useRef, useEffect } from 'react';
import styles from './AgentFreeform.styles';
import { AgentConversation, AgentChatMessage, AgentActionType, AgentChatMessageType } from './Agent.types';
import { AgentChat } from './AgentChat/AgentChat';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

export interface AgentFreeformProps {
  conversations?: Map<string, AgentConversation>;
  onSendMessage?: (message: { convoId: string; message: AgentChatMessage }) => void;
}

const AgentFreeform: React.FC<AgentFreeformProps> = ({ conversations = new Map(), onSendMessage }) => {
  const [inputValue, setInputValue] = useState('');
  const [currentConvoId, setCurrentConvoId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Always use the last conversation
  useEffect(() => {
    if (conversations.size > 0) {
      const lastConversation = [...conversations.values()][conversations.size - 1];
      setCurrentConvoId(lastConversation.convoId);
    }
  }, [conversations]);

  // Get current conversation's messages
  const currentConversation = conversations.get(currentConvoId);
  const messages: AgentChatMessage[] = currentConversation?.messages ?? [];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, []);

  const handleSendMessage = () => {
    if (inputValue.trim() === '' || !currentConvoId) return;

    const newMessage: AgentChatMessage = {
      user: 'user',
      type: AgentChatMessageType.user,
      convoId: currentConvoId,
      message: inputValue,
      actions: [],
    };

    if (onSendMessage) {
      onSendMessage({ convoId: currentConvoId, message: newMessage });
    }

    setInputValue('');
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className={styles.agentFreeformContainer}>
      <div className={styles.messagesContainer}>
        {messages.map((message, idx) => (
          <AgentChat key={idx} type={message.type} message={message.message ?? ''} />
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className={styles.inputContainer}>
        <input
          type="text"
          className={styles.inputField}
          placeholder="Ask anything"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
        />
        <button
          className={styles.sendButton}
          onClick={handleSendMessage}
          disabled={inputValue.trim() === '' || !currentConvoId}
        >
          <i className="fas fa-arrow-up" />
        </button>
      </div>
    </div>
  );
};

export default AgentFreeform;
